import { MultiplayerService, GameRoom } from './MultiplayerService';
import { CampaignType, FactionType } from './types';
import { debug } from './debug';
import { sanitizeRoomName, validateFaction } from './security';

export class MultiplayerLobby {
  private container: HTMLElement;
  private multiplayerService: MultiplayerService;
  private selectedCampaign: CampaignType;
  private refreshTimer: any = null;
  private onGameStart: (room: GameRoom) => void;
  private onCancel: () => void;

  constructor(
    multiplayerService: MultiplayerService,
    selectedCampaign: CampaignType,
    onGameStart: (room: GameRoom) => void,
    onCancel: () => void
  ) {
    this.multiplayerService = multiplayerService;
    this.selectedCampaign = selectedCampaign;
    this.onGameStart = onGameStart;
    this.onCancel = onCancel;
    this.container = this.createLobbyUI();
  }

  private createLobbyUI(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'multiplayer-lobby';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 50000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      font-family: Arial, sans-serif;
    `;

    const container = document.createElement('div');
    container.style.cssText = `
      width: 90%;
      max-width: 800px;
      max-height: 80vh;
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border-radius: 10px;
      padding: 30px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      overflow-y: auto;
    `;

    // Title
    const title = document.createElement('h2');
    title.textContent = 'Multiplayer Lobby';
    title.style.cssText = `
      font-size: 32px;
      margin-bottom: 10px;
      text-align: center;
      color: #fff;
    `;
    container.appendChild(title);
    
    // Campaign indicator
    const campaignInfo = document.createElement('p');
    campaignInfo.textContent = `Campaign: ${this.getCampaignDisplayName(this.selectedCampaign)}`;
    campaignInfo.style.cssText = `
      font-size: 18px;
      margin-bottom: 20px;
      text-align: center;
      color: #4CAF50;
    `;
    container.appendChild(campaignInfo);

    // Tab buttons
    const tabContainer = document.createElement('div');
    tabContainer.style.cssText = `
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
      justify-content: center;
    `;

    const createTabBtn = document.createElement('button');
    createTabBtn.textContent = 'Create Game';
    createTabBtn.style.cssText = this.getButtonStyle(true);
    
    const joinTabBtn = document.createElement('button');
    joinTabBtn.textContent = 'Join Game';
    joinTabBtn.style.cssText = this.getButtonStyle(false);

    tabContainer.appendChild(createTabBtn);
    tabContainer.appendChild(joinTabBtn);
    container.appendChild(tabContainer);

    // Content area
    const contentArea = document.createElement('div');
    contentArea.id = 'lobby-content';
    container.appendChild(contentArea);

    // Initially show create game tab
    this.showCreateGame(contentArea);

    // Tab switching
    createTabBtn.onclick = () => {
      createTabBtn.style.cssText = this.getButtonStyle(true);
      joinTabBtn.style.cssText = this.getButtonStyle(false);
      this.stopRefresh(); // Stop any existing refresh timer
      this.showCreateGame(contentArea);
    };

    joinTabBtn.onclick = () => {
      createTabBtn.style.cssText = this.getButtonStyle(false);
      joinTabBtn.style.cssText = this.getButtonStyle(true);
      this.stopRefresh(); // Stop any existing refresh timer
      this.showJoinGame(contentArea);
    };

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Back to Menu';
    cancelBtn.style.cssText = `
      margin-top: 20px;
      padding: 10px 20px;
      background: #666;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 16px;
      width: 100%;
    `;
    cancelBtn.onmouseover = () => cancelBtn.style.background = '#777';
    cancelBtn.onmouseout = () => cancelBtn.style.background = '#666';
    cancelBtn.onclick = () => {
      this.stopRefresh();
      this.onCancel();
      overlay.remove();
    };
    container.appendChild(cancelBtn);

    overlay.appendChild(container);
    return overlay;
  }

  private getButtonStyle(active: boolean): string {
    return `
      padding: 10px 20px;
      background: ${active ? '#4CAF50' : '#333'};
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 16px;
      transition: background 0.3s;
    `;
  }

  private showCreateGame(contentArea: HTMLElement) {
    contentArea.innerHTML = '';

    // Room name input
    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Room Name:';
    nameLabel.style.cssText = 'display: block; margin-bottom: 5px; color: #ccc;';
    
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = `Room ${Math.floor(Math.random() * 1000)}`;
    nameInput.style.cssText = `
      width: 100%;
      padding: 10px;
      margin-bottom: 15px;
      background: #333;
      color: white;
      border: 1px solid #555;
      border-radius: 5px;
    `;


    // Faction selection
    const factionLabel = document.createElement('label');
    factionLabel.textContent = 'Your Faction:';
    factionLabel.style.cssText = 'display: block; margin-bottom: 5px; color: #ccc;';
    
    const factionSelect = document.createElement('select');
    factionSelect.style.cssText = `
      width: 100%;
      padding: 10px;
      margin-bottom: 20px;
      background: #333;
      color: white;
      border: 1px solid #555;
      border-radius: 5px;
    `;

    // Add factions for the selected campaign
    const factions = this.getFactionsForCampaign(this.selectedCampaign);
    factions.forEach(faction => {
      const option = document.createElement('option');
      option.value = faction;
      option.textContent = faction;
      factionSelect.appendChild(option);
    });

    // Create button
    const createBtn = document.createElement('button');
    createBtn.textContent = 'Create Room';
    createBtn.style.cssText = `
      width: 100%;
      padding: 12px;
      background: #4CAF50;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 18px;
    `;
    createBtn.onmouseover = () => createBtn.style.background = '#45a049';
    createBtn.onmouseout = () => createBtn.style.background = '#4CAF50';
    
    createBtn.onclick = async () => {
      try {
        createBtn.disabled = true;
        createBtn.textContent = 'Creating...';
        
        // Sanitize and validate inputs before sending
        const sanitizedName = sanitizeRoomName(nameInput.value);
        const validatedFaction = validateFaction(factionSelect.value);
        
        debug.log('Creating room with:', {
          name: sanitizedName,
          campaign: this.selectedCampaign,
          faction: validatedFaction
        });
        
        // Add timeout to prevent infinite waiting
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Connection timeout - please check your internet connection')), 10000);
        });
        
        const room = await Promise.race([
          this.multiplayerService.createRoom(
            sanitizedName,
            this.selectedCampaign,
            validatedFaction as FactionType
          ),
          timeoutPromise
        ]) as GameRoom;

        if (!room) {
          throw new Error('Room creation returned null');
        }

        this.showWaitingRoom(contentArea, room);
      } catch (error: any) {
        debug.error('Failed to create room:', error);
        const errorMessage = error?.message || 'Unknown error occurred';
        alert(`Failed to create room: ${errorMessage}\n\nPlease check your internet connection and try again.`);
        createBtn.disabled = false;
        createBtn.textContent = 'Create Room';
      }
    };

    contentArea.appendChild(nameLabel);
    contentArea.appendChild(nameInput);
    contentArea.appendChild(factionLabel);
    contentArea.appendChild(factionSelect);
    contentArea.appendChild(createBtn);
  }

  private showJoinGame(contentArea: HTMLElement) {
    contentArea.innerHTML = '';

    const roomsContainer = document.createElement('div');
    roomsContainer.style.cssText = `
      max-height: 400px;
      overflow-y: auto;
      margin-bottom: 15px;
    `;

    const refreshBtn = document.createElement('button');
    refreshBtn.textContent = 'Refresh';
    refreshBtn.style.cssText = `
      padding: 8px 16px;
      background: #555;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      margin-bottom: 15px;
    `;
    refreshBtn.onclick = () => this.loadRooms(roomsContainer);

    contentArea.appendChild(refreshBtn);
    contentArea.appendChild(roomsContainer);

    // Load rooms initially (no auto-refresh to prevent disrupting joins)
    this.loadRooms(roomsContainer);
  }

  private async loadRooms(container: HTMLElement) {
    container.innerHTML = '<p style="color: #ccc;">Loading rooms...</p>';

    try {
      const allRooms = await this.multiplayerService.listRooms();
      
      // Filter rooms by selected campaign and exclude full rooms
      const rooms = allRooms.filter(room => 
        room.campaign === this.selectedCampaign && 
        room.status === 'waiting' // Only show rooms that are waiting for players
      );
      
      if (rooms.length === 0) {
        container.innerHTML = `<p style="color: #999;">No ${this.getCampaignDisplayName(this.selectedCampaign)} rooms available. Create one!</p>`;
        return;
      }

      container.innerHTML = '';
      rooms.forEach(room => {
        const roomCard = document.createElement('div');
        roomCard.style.cssText = `
          background: #333;
          border: 1px solid #555;
          border-radius: 5px;
          padding: 15px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: background 0.3s;
        `;
        roomCard.onmouseover = () => roomCard.style.background = '#444';
        roomCard.onmouseout = () => roomCard.style.background = '#333';

        const roomInfo = document.createElement('div');
        
        // Create elements safely to prevent XSS
        const roomTitle = document.createElement('h3');
        roomTitle.style.cssText = 'margin: 0 0 10px 0; color: #4CAF50;';
        roomTitle.textContent = sanitizeRoomName(room.name);
        
        const hostInfo = document.createElement('p');
        hostInfo.style.cssText = 'margin: 5px 0; color: #ccc;';
        hostInfo.textContent = `Host: ${room.host.name} (${room.host.faction})`;
        
        const statusInfo = document.createElement('p');
        statusInfo.style.cssText = 'margin: 5px 0; color: #ffa500;';
        statusInfo.textContent = 'Waiting for player...';
        
        roomInfo.appendChild(roomTitle);
        roomInfo.appendChild(hostInfo);
        roomInfo.appendChild(statusInfo);

        roomCard.appendChild(roomInfo);
        roomCard.onclick = () => this.joinRoom(room, container);
        container.appendChild(roomCard);
      });
    } catch (error) {
      debug.error('Failed to load rooms:', error);
      // Safe error message display
      const errorMsg = document.createElement('p');
      errorMsg.style.cssText = 'color: #f44;';
      errorMsg.textContent = 'Failed to load rooms';
      container.appendChild(errorMsg);
    }
  }

  private async joinRoom(room: GameRoom, container: HTMLElement) {
    // Show faction selection for joining
    container.innerHTML = '';
    
    const title = document.createElement('h3');
    title.textContent = `Joining: ${room.name}`;
    title.style.cssText = 'color: #4CAF50; margin-bottom: 15px;';
    
    const factionLabel = document.createElement('label');
    factionLabel.textContent = 'Select Your Faction:';
    factionLabel.style.cssText = 'display: block; margin-bottom: 5px; color: #ccc;';
    
    const factionSelect = document.createElement('select');
    factionSelect.style.cssText = `
      width: 100%;
      padding: 10px;
      margin-bottom: 15px;
      background: #333;
      color: white;
      border: 1px solid #555;
      border-radius: 5px;
    `;

    // Get available factions (excluding host's faction)
    const factions = this.getFactionsForCampaign(room.campaign)
      .filter(f => f !== room.host.faction);
    
    factions.forEach(faction => {
      const option = document.createElement('option');
      option.value = faction;
      option.textContent = faction;
      factionSelect.appendChild(option);
    });

    const joinBtn = document.createElement('button');
    joinBtn.textContent = 'Join Room';
    joinBtn.style.cssText = `
      width: 100%;
      padding: 12px;
      background: #4CAF50;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 18px;
    `;

    joinBtn.onclick = async () => {
      try {
        joinBtn.disabled = true;
        joinBtn.textContent = 'Joining...';
        
        const joinedRoom = await this.multiplayerService.joinRoom(
          room.id,
          factionSelect.value as FactionType
        );

        this.stopRefresh();
        this.onGameStart(joinedRoom);
        this.container.remove();
      } catch (error: any) {
        debug.error('Failed to join room:', error);
        alert(error.message || 'Failed to join room');
        joinBtn.disabled = false;
        joinBtn.textContent = 'Join Room';
      }
    };

    container.appendChild(title);
    container.appendChild(factionLabel);
    container.appendChild(factionSelect);
    container.appendChild(joinBtn);
  }

  private showWaitingRoom(contentArea: HTMLElement, room: GameRoom) {
    contentArea.innerHTML = '';

    const waitingDiv = document.createElement('div');
    waitingDiv.style.cssText = 'text-align: center;';

    const title = document.createElement('h3');
    title.textContent = 'Waiting for opponent...';
    title.style.cssText = 'color: #4CAF50; margin-bottom: 20px;';

    const roomInfo = document.createElement('div');
    roomInfo.style.cssText = 'color: #ccc; margin-bottom: 20px;';
    roomInfo.innerHTML = `
      <p>Room: ${room.name}</p>
      <p>Campaign: ${room.campaign}</p>
      <p>Your Faction: ${room.host.faction}</p>
      <p>Room ID: ${room.id}</p>
    `;

    const spinner = document.createElement('div');
    spinner.style.cssText = `
      border: 4px solid #333;
      border-top: 4px solid #4CAF50;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
      margin: 20px auto;
    `;

    // Add spinner animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    waitingDiv.appendChild(title);
    waitingDiv.appendChild(roomInfo);
    waitingDiv.appendChild(spinner);
    contentArea.appendChild(waitingDiv);

    // Listen for room updates
    this.multiplayerService.onRoomUpdate((updatedRoom) => {
      if (updatedRoom.status === 'ready' && updatedRoom.guest) {
        this.stopRefresh();
        this.onGameStart(updatedRoom);
        this.container.remove();
      }
    });
  }

  private getCampaignDisplayName(campaign: CampaignType): string {
    switch (campaign) {
      case CampaignType.NORMANDY:
        return 'Normandy';
      case CampaignType.TUNISIA:
        return 'Tunisia';
      case CampaignType.STALINGRAD:
        return 'Stalingrad';
      case CampaignType.OKINAWA:
        return 'Okinawa';
      case CampaignType.CAMPOBASSO:
        return 'Campobasso';
      default:
        return 'Unknown';
    }
  }
  
  private getFactionsForCampaign(campaign: CampaignType): FactionType[] {
    switch (campaign) {
      case CampaignType.NORMANDY:
        return ['United States Of America', '3rd Reich'];
      case CampaignType.TUNISIA:
        return ['United Kingdom', '3rd Reich'];
      case CampaignType.STALINGRAD:
        return ['USSR', '3rd Reich'];
      case CampaignType.OKINAWA:
        return ['United States Of America', 'Imperial Japan'];
      case CampaignType.CAMPOBASSO:
        return ['Canada', 'Fascist Italy'];
      default:
        return ['United States Of America', '3rd Reich'];
    }
  }

  private stopRefresh() {
    // Keep this method since it's called in tab switching
    // But it doesn't do anything since we removed auto-refresh
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  public show() {
    document.body.appendChild(this.container);
  }

  public hide() {
    this.stopRefresh();
    this.container.remove();
  }
}